/** Geometry helpers (prototype "geometry" section). Pure. */
import { POLICY, SNAP_PER_FT } from './policy';
import type { Corner, Edge, EdgeAxis, Plan, Pt, Room } from './types';

/** The core's coordinate precision (policy `coord_per_ft`): corners round to it, geometry compares within it. */
const COORD_PER_FT = POLICY.coord_per_ft;
export const EPS = 1 / COORD_PER_FT;

/** Structural counts, not policy: a polygon has at least 3 corners, a closed
 * square-cornered room at least 4, an L exactly 6 on a 3 x 3 coordinate grid. */
export const MIN_POLY_CORNERS = 3; // policy-literal-ok: structural
export const RECT_CORNERS = 4; // policy-literal-ok: structural
export const L_CORNERS = 6; // policy-literal-ok: structural
export const L_GRID_VALUES = 3; // policy-literal-ok: structural

/** Nearest snap step (whole inch by default). */
export const snapIn = (v: number): number => Math.round(v * SNAP_PER_FT) / SNAP_PER_FT;
export const clamp = (v: number, a: number, b: number): number => Math.max(a, Math.min(b, v));
export const dist = (a: Pt, b: Pt): number => Math.hypot(b.x - a.x, b.y - a.y);
/** Round to the core's coordinate precision (1e-6 by default; feet for corners, inches for recorded adjustments). */
export const round6 = (v: number): number => Math.round(v * COORD_PER_FT) / COORD_PER_FT;

export function findRoom(plan: Plan, id: string): Room | undefined {
  return plan.rooms.find((r) => r.id === id);
}
export const vIdx = (r: Room, vid: number): number => r.corners.findIndex((v) => v.id === vid);

export function edgeOf(r: Room, vid: number): Edge | null {
  const i = vIdx(r, vid);
  if (i < 0) return null;
  const n = r.corners.length;
  return { i, a: r.corners[i], b: r.corners[(i + 1) % n] };
}

export function axisOf(a: Pt, b: Pt): EdgeAxis {
  const dx = Math.abs(b.x - a.x), dy = Math.abs(b.y - a.y);
  if (dx < EPS && dy < EPS) return 'z';
  if (dy < EPS) return 'h';
  if (dx < EPS) return 'v';
  return 'd';
}

export function areaPts(pts: Pt[]): number {
  let s = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    s += a.x * b.y - b.x * a.y;
  }
  return s / 2;
}
/** Signed shoelace area (positive for the core's default winding). */
export const area = (r: Room): number => areaPts(r.corners);

export function unit(a: Pt, b: Pt): Pt {
  const L = dist(a, b) || 1;
  return { x: (b.x - a.x) / L, y: (b.y - a.y) / L };
}

/** Outward normal of edge a->b of room r. */
export function outN(r: Room, a: Pt, b: Pt, A?: number): Pt {
  const ar = A ?? area(r);
  const u = unit(a, b);
  return ar > 0 ? { x: u.y, y: -u.x } : { x: -u.y, y: u.x };
}

export function perim(r: Room): number {
  let s = 0;
  const n = r.corners.length;
  for (let i = 0; i < n; i++) s += dist(r.corners[i], r.corners[(i + 1) % n]);
  return s;
}

export function inPolyPts(pts: Pt[], p: Pt): boolean {
  let c = false;
  const n = pts.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = pts[i], b = pts[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) c = !c;
  }
  return c;
}
export const inPoly = (r: Room, p: Pt): boolean => inPolyPts(r.corners, p);

export function segDist(p: Pt, a: Pt, b: Pt): number {
  const L2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  if (L2 < 1e-12) return dist(p, a);
  const t = clamp(((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / L2, 0, 1);
  return dist(p, { x: a.x + t * (b.x - a.x), y: a.y + t * (b.y - a.y) });
}

/** Where the room's name label goes: centroid, or the widest span at its height. */
export function labelPt(r: Room): Pt {
  const A = area(r);
  let cx = 0, cy = 0;
  const n = r.corners.length;
  for (let i = 0; i < n; i++) {
    const a = r.corners[i], b = r.corners[(i + 1) % n], f = a.x * b.y - b.x * a.y;
    cx += (a.x + b.x) * f;
    cy += (a.y + b.y) * f;
  }
  const p = Math.abs(A) > 1e-9 ? { x: cx / (6 * A), y: cy / (6 * A) } : { x: r.corners[0].x, y: r.corners[0].y };
  if (inPoly(r, p)) return p;
  const xs: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = r.corners[i], b = r.corners[(i + 1) % n];
    if ((a.y > p.y) !== (b.y > p.y)) xs.push(a.x + ((p.y - a.y) * (b.x - a.x)) / (b.y - a.y));
  }
  xs.sort((a, b) => a - b);
  let best: [number, number] | null = null;
  for (let i = 0; i + 1 < xs.length; i += 2) {
    if (!best || xs[i + 1] - xs[i] > best[1] - best[0]) best = [xs[i], xs[i + 1]];
  }
  return best ? { x: (best[0] + best[1]) / 2, y: p.y } : p;
}

/** Point at distance s along edge a->b. */
export function along(a: Pt, b: Pt, s: number): Pt {
  const u = unit(a, b);
  return { x: a.x + u.x * s, y: a.y + u.y * s };
}

/** Projected, snapped, clamped offset of world point wp along edge `vid`. */
export function projOff(r: Room, vid: number, wp: Pt): number {
  const E = edgeOf(r, vid);
  if (!E) return 0;
  const u = unit(E.a, E.b);
  return snapIn(clamp((wp.x - E.a.x) * u.x + (wp.y - E.a.y) * u.y, 0, dist(E.a, E.b)));
}

/** Round every coordinate to the core's precision, 1e-6 ft (the prototype's `changed()`). */
export function tidy(plan: Plan): void {
  for (const r of plan.rooms) for (const v of r.corners) { v.x = round6(v.x); v.y = round6(v.y); }
}

export function bbox(pts: Pt[]): [number, number, number, number] {
  const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

export const clone = <T>(o: T): T => JSON.parse(JSON.stringify(o)) as T;

